/**
 * A customer's default work order forms, one per job type (2026-10-29,
 * Corbin approved; mockup threads/wo-default-form/mockup.html).
 *
 * When a job's own work order matched no form, GWC fills the customer's
 * default for that job's TYPE. A test and a repair use different Cintas
 * paper, so each kind of job gets its own default. Repair (both sizes) and
 * Replacement (both sizes) are one pick each. A repair never borrows the test
 * default — with none set, the job waits with "Needs a return form".
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { InfoButton, type InfoTopic } from "@/components/info-button";

export interface DefaultGroup {
    key: string;
    label: string;
    tradeName: string | null;
    jobTypeIds: string[];
    coversSizes: boolean;
    usedByCustomer: boolean;
    templateId: string | null;
    templateName: string | null;
    mixed: boolean;
}

export const WO_DEFAULTS_HELP: InfoTopic = {
    title: "What a default form is",
    lines: [
        "A default is the form GWC fills when a job's work order didn't match any form. Pick one for each kind of job.",
        'A repair job only uses the repair default. If none is set, the job waits on Close Out with "Needs a return form".',
    ],
};

/** Color carries the kind of job: test blue, repair amber, replacement purple, clean & flush teal. */
export function groupTagClass(key: string): string {
    if (key.startsWith("backflow_test")) return "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200";
    if (key === "backflow_repair") return "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200";
    if (key === "backflow_replace") return "bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200";
    if (key.startsWith("backflow_clean")) return "bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200";
    return "bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200";
}

/** "Cintas — PM - Back Flow - Annual" → "PM - Back Flow - Annual": the vendor is already on the page. */
export function shortFormName(name: string | null | undefined): string {
    const n = name ?? "";
    const i = n.search(/\s[—–]\s/);
    return i >= 0 ? n.slice(i + 3).trim() : n;
}

/** Which picks a form is the default for. */
export function defaultsForForm(groups: DefaultGroup[], templateId: string): DefaultGroup[] {
    return groups.filter((g) => g.templateId === templateId);
}

/** The job type ids to save when these picks are checked. */
export function jobTypeIdsFor(groups: DefaultGroup[], checkedKeys: Set<string>): string[] {
    return groups.filter((g) => checkedKeys.has(g.key)).flatMap((g) => g.jobTypeIds);
}

/** The strip: picks with a default, plus this customer's kinds of work with none. */
export function stripGroups(groups: DefaultGroup[]): DefaultGroup[] {
    return groups.filter((g) => g.templateId || g.usedByCustomer);
}

export function JobTypeTag({ group, star }: { group: Pick<DefaultGroup, "key" | "label">; star?: boolean }) {
    return (
        <span
            className={cn(
                "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold",
                groupTagClass(group.key),
            )}
        >
            {star && <span aria-hidden>★</span>}
            {group.label}
        </span>
    );
}

export function WoDefaultsStrip({ groups }: { groups: DefaultGroup[] }) {
    const shown = stripGroups(groups);
    return (
        <div className="mb-3 space-y-2" data-testid="wo-defaults-strip">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Default form for each kind of job</span>
                <InfoButton topic={WO_DEFAULTS_HELP} />
            </div>
            {shown.length === 0 ? (
                <p className="text-sm text-foreground/70">
                    None set yet. Press &quot;Make default for…&quot; on a locked form below.
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {shown.map((g) => (
                        <span
                            key={g.key}
                            className={cn(
                                "flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-sm",
                                !g.templateId && "border-dashed",
                            )}
                        >
                            <JobTypeTag group={g} />
                            <span className="text-foreground/50">→</span>
                            {g.templateId ? (
                                <span>{shortFormName(g.templateName)}</span>
                            ) : (
                                <span className="font-semibold text-amber-700 dark:text-amber-300">
                                    {g.mixed ? "differs by size" : "none set"}
                                </span>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export function DefaultForTags({ groups, templateId }: { groups: DefaultGroup[]; templateId: string }) {
    const mine = defaultsForForm(groups, templateId);
    if (mine.length === 0) return null;
    return (
        <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid="wo-default-for">
            <span className="text-xs text-foreground/70">Default for:</span>
            {mine.map((g) => (
                <JobTypeTag key={g.key} group={g} star />
            ))}
        </div>
    );
}

/** "Use this form when the job is a…" — the pick list under a locked form. */
export function MakeDefaultForPicker({
    customerId,
    templateId,
    groups,
    onDone,
}: {
    customerId: string;
    templateId: string;
    groups: DefaultGroup[];
    onDone: () => void;
}) {
    const qc = useQueryClient();
    const [checked, setChecked] = useState<Set<string>>(
        () => new Set(defaultsForForm(groups, templateId).map((g) => g.key)),
    );
    const save = useMutation({
        mutationFn: () =>
            api.put(`/api/customers/${customerId}/work-order-defaults`, {
                templateId,
                jobTypeIds: jobTypeIdsFor(groups, checked),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "work-order-defaults"] });
            toast.success("Default forms saved.");
            onDone();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't save the default forms."),
    });
    const toggle = (key: string) =>
        setChecked((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    const theirs = groups.filter((g) => g.usedByCustomer);
    const rest = groups.filter((g) => !g.usedByCustomer);

    const row = (g: DefaultGroup) => {
        const on = checked.has(g.key);
        const elsewhere = g.templateId && g.templateId !== templateId ? shortFormName(g.templateName) : null;
        return (
            <label
                key={g.key}
                className="flex cursor-pointer items-center gap-3 border-t px-1 py-1.5 first:border-t-0"
                data-testid={`wo-default-pick-${g.key}`}
            >
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={on} onChange={() => toggle(g.key)} />
                <JobTypeTag group={g} />
                {g.coversSizes && <span className="text-xs text-foreground/60">both sizes</span>}
                <span
                    className={cn(
                        "ml-auto text-right text-xs",
                        on && elsewhere ? "font-semibold text-amber-700 dark:text-amber-300" : "text-foreground/60",
                    )}
                >
                    {g.templateId === templateId
                        ? on
                            ? "this form"
                            : "will be cleared"
                        : elsewhere
                          ? on
                              ? `moves here from ${elsewhere}`
                              : `now: ${elsewhere}`
                          : g.mixed
                            ? "differs by size"
                            : "none set"}
                </span>
            </label>
        );
    };

    return (
        <div className="mt-2 rounded-lg border border-primary p-3 shadow-sm" data-testid="wo-default-picker">
            <p className="mb-2 text-sm font-semibold">Use this form when the job is a…</p>
            {theirs.length > 0 && (
                <>
                    <p className="mt-1 text-xs uppercase tracking-wide text-foreground/60">This customer&apos;s jobs</p>
                    <div>{theirs.map(row)}</div>
                </>
            )}
            {rest.length > 0 && (
                <>
                    <p className="mt-3 text-xs uppercase tracking-wide text-foreground/60">
                        {theirs.length > 0 ? "Other job types" : "Job types"}
                    </p>
                    <div>{rest.map(row)}</div>
                </>
            )}
            <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={onDone} disabled={save.isPending}>
                    Cancel
                </Button>
                <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                    Save
                </Button>
            </div>
        </div>
    );
}
